 
import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { LoadState, MODULE_IDS } from "../../../../../../constants/enums";
import { StandadCard } from "../../../../../../components/Cards";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomCheckBoxWithLabel,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";
import { useCommAutovehcilesList, useCustomerActivePolicies, useDispatchWrapper, useCustomerPolicyDetails } from "../../../../../../hooks";
import { useFormik } from "formik";

import { Add, Close, Remove } from "@mui/icons-material";
import {
  getFullAddres,
  getUniqueId,
} from "../../../../../../helpers";
import produce from "immer";
import { useNavigate } from "react-router-dom";
import { PhoneTextField } from "../../../../../../components/PhoneTextField/PhoneTextField";
import { UsersAutoSearchByRole } from "../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { useCurrentUserId } from "../../../../../../hooks/useAssignedTo";
import { clearBusinessPolicyRestrutant } from "../../../../../../redux/businessPolicy/businessPolicyRestrutant/businessPolicyRestrutantActions";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import {
  addBusinessLiabilityAsync,
  clearBusinessPolicyFormsState,
  fetchBusinessLiabilityAsync,
} from "../../../../../../redux/businessPolicy/businessPolicyForms/businessPolicyFormsActions";
import { IBusinessPolicyLiability } from "../../../../../../redux/businessPolicy/businessPolicyForms/businessPolicyForms.types";
import { FieldSet } from "../../../../../../components/FieldSet/FieldSet";
import { AutoComplete } from "../../../../../../components/AutoCompleteSearches/AutoComplete";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { fetchInsurerCodeWithCallbackAsync } from "../../../../../../redux/insurer/insurerActions";

// Thin, numbered, uppercase section header bar matching the printed
// certificate mock (square corners, dark-blue, dense). Mirrors the header
// used by the Certificate of Property Insurance so both certificates share
// the same visual language.
const SectionBar: React.FC<{
  no?: number | string;
  title: string;
  right?: React.ReactNode;
}> = ({ no, title, right }) => (
  <Box
    sx={{
      backgroundColor: "primary.main",
      color: "#fff",
      px: 1.5,
      minHeight: 36,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <Typography
      sx={{
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {no !== undefined ? `${no}. ` : ""}
      {title}
    </Typography>
    {right}
  </Box>
);

interface ICSIOCertificateProps {
  customerId?: number;
  customerPolicyId?: number;
  liabilityNumber?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (data: IBusinessPolicyLiability) => void;
  isDuplicate?: boolean;
  initialData?: IBusinessPolicyLiability;
  disableApiCalls?: boolean;
  disableSaveApi?: boolean;
}

export const CSIOCertificate: React.FC<ICSIOCertificateProps> = (props) => {
  const {
    customerId,
    customerPolicyId,
    liabilityNumber,
    staticStatusValue,
    onSaveSuccess,
    readOnly = false,
    fromRightPanel = false,
    created_from = "Liability_Certificate",
    initialData,
    disableApiCalls = false,
    disableSaveApi = false,
  } = props;
  const { isDuplicate } = props;
  const isUpdate = liabilityNumber ? true : false;
  const dispatch = useDispatchWrapper();

  const {
    data: businessPolicyLiability,
    loading: businessPolicyLiabilityLoading,
  } = useSelector(
    (storeState: IStoreState) => storeState.business.policyForms.liability
  );
  const { data: customerPolicies } = useCustomerActivePolicies(customerId);
  const {details: policyAdmin,  insurer, branch } = useCustomerPolicyDetails(customerPolicyId);

  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const currentUserId = useCurrentUserId();

  const {
    values,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
    errors,
  } = useFormik({
    initialValues: businessPolicyLiability,
    validate: (values) => {
      const errors: any = {};
      if (!values.certificate_authorization_phone_number) {
        errors.certificate_authorization_phone_number =
          "*Phone number is required.";
      } else if (
        values.certificate_authorization_phone_number &&
        values.certificate_authorization_phone_number.length !== 10
      ) {
        errors.certificate_authorization_phone_number =
          "*Phone number should be 10 digits.";
      } else if (!values.certificate_authorization_email) {
        errors.certificate_authorization_email = "*Email is required.";
      } else if (
        values.certificate_authorization_email !== "" &&
        !/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(
          values.certificate_authorization_email
        )
      ) {
        errors.certificate_authorization_email = "*Invalid email address.";
      }
      return errors;
    },
    onSubmit: (values) => {
      setSaveLoading(true);
      if (disableSaveApi) {
        onSaveSuccess(values);
      } else {
        if (customerId && customerPolicyId) {
          let finalStatus = values.status;
          if (staticStatusValue) {
            finalStatus = staticStatusValue;
          }
          dispatch(
            addBusinessLiabilityAsync(
              {
                ...values,
                customer_id: customerId || 0,
                producer_one_id: policyAdmin?.data?.producer_one,
                producer_one_code: policyAdmin?.data?.producer_one_code,
                customer_policy_id: customerPolicyId,
                status: finalStatus,
                created_from: created_from,
              },
              businessPolicyLiability,
              isUpdate,
              (isSuccess, data) => {
                if (isSuccess && data) {
                  window.scrollTo(0, 0);
                  onSaveSuccess(data);
                }
                setSaveLoading(false);
              },
              isDuplicate
            )
          );
        }
      }
    },
  });

  const { vehicleDropdownlist } = useCommAutovehcilesList(
    false,
    values.automobile_policy || undefined
  );

  const handleCGLPolicy = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.cgl_policy_number = value;
        draftValues.commercial_general_liability_section_one.policy_number =
          value;
        draftValues.commercial_general_liability_section_one.insurance_company =
          "";
        draftValues.commercial_general_liability_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.commercial_general_liability_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "commercial_general_liability_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.cgl_policy_number = "";
        draftValues.commercial_general_liability_section_one.policy_number = "";
        draftValues.commercial_general_liability_section_one.insurance_company =
          "";
        draftValues.commercial_general_liability_section_one.effective_date =
          "";
        draftValues.commercial_general_liability_section_one.expiry_date = "";
      });
      setValues(newValues);
    }
  };

  const handleNonOwnedAutomobile = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.non_owned_automobiles_policy = value;
        draftValues.non_owned_automobiles_section_one.policy_number = value;
        draftValues.non_owned_automobiles_section_one.insurance_company = "";
        draftValues.non_owned_automobiles_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.non_owned_automobiles_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "non_owned_automobiles_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.non_owned_automobiles_policy = "";
        draftValues.non_owned_automobiles_section_one.policy_number = "";
        draftValues.non_owned_automobiles_section_one.insurance_company = "";
        draftValues.non_owned_automobiles_section_one.effective_date = "";
        draftValues.non_owned_automobiles_section_one.expiry_date = "";
      });
      setValues(newValues);
    }
  };

  const handleHiredAutomobile = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.hired_automobiles = value;
        draftValues.hired_automobiles_section_one.policy_number = value;
        draftValues.hired_automobiles_section_one.insurance_company = "";
        draftValues.hired_automobiles_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.hired_automobiles_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "hired_automobiles_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.hired_automobiles = "";
        draftValues.hired_automobiles_section_one.policy_number = "";
        draftValues.hired_automobiles_section_one.insurance_company = "";
        draftValues.hired_automobiles_section_one.effective_date = "";
        draftValues.hired_automobiles_section_one.expiry_date = "";
      });
      setValues(newValues);
    }
  };

  const handleAutomobilemobile = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.automobile_policy = value;
        draftValues.automobile_liability_section_one.policy_number = value;
        draftValues.automobile_liability_section_one.insurance_company = "";
        draftValues.automobile_liability_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.automobile_liability_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
        draftValues.vehicle_unit_number_or_vin = null;
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "automobile_liability_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.automobile_policy = "";
        draftValues.automobile_liability_section_one.policy_number = "";
        draftValues.automobile_liability_section_one.insurance_company = "";
        draftValues.automobile_liability_section_one.effective_date = "";
        draftValues.automobile_liability_section_one.expiry_date = "";
        draftValues.vehicle_unit_number_or_vin = null;
      });
      setValues(newValues);
    }
  };

  const handleOtherPolicy = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.other_policy = value;
        draftValues.other_liability_section_one.policy_number = value;
        draftValues.other_liability_section_one.insurance_company = "";
        draftValues.other_liability_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.other_liability_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "other_liability_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.other_policy = "";
        draftValues.other_liability_section_one.policy_number = "";
        draftValues.other_liability_section_one.insurance_company = "";
        draftValues.other_liability_section_one.effective_date = "";
        draftValues.other_liability_section_one.expiry_date = "";
      });
      setValues(newValues);
    }
  };

  const handleExcessPolicy = (value: string | null) => {
    if (value) {
      const selectedPolicy = customerPolicies.find(
        (p) => p.policy_number === value
      );
      const newValues = produce(values, (draftValues) => {
        draftValues.umbrella_or_excess_liability_policy = value;
        draftValues.excess_liability_section_one.policy_number = value;
        draftValues.excess_liability_section_one.insurance_company = "";
        draftValues.excess_liability_section_one.effective_date =
          selectedPolicy?.effective_date || "";
        draftValues.excess_liability_section_one.expiry_date =
          selectedPolicy?.expiry_date || "";
      });
      setValues(newValues);
      if (selectedPolicy?.insurer) {
        dispatch(
          fetchInsurerCodeWithCallbackAsync(selectedPolicy.insurer, (data) => {
            setFieldValue(
              "excess_liability_section_one.insurance_company",
              data.name
            );
          })
        );
      }
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.umbrella_or_excess_liability_policy = "";
        draftValues.excess_liability_section_one.policy_number = "";
        draftValues.excess_liability_section_one.insurance_company = "";
        draftValues.excess_liability_section_one.effective_date = "";
        draftValues.excess_liability_section_one.expiry_date = "";
      });
      setValues(newValues);
    }
  };

  const handleAdd = () => {
    const finalLines = [...values.description_of_operations];
    finalLines.push({
      key: getUniqueId(),
      label: "Line " + (values.description_of_operations.length + 1),
      value: "",
    });
    setFieldValue("description_of_operations", finalLines);
  };

  const handleRemove = () => {
    const finalLines = [...values.description_of_operations];
    finalLines.pop();
    setFieldValue("description_of_operations", finalLines);
  };
  const handleDescriptionLine =
    (key: string, index: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const finalLines = produce(
        values.description_of_operations,
        (draftLines) => {
          draftLines[index]["value"] = value;
        }
      );

      setFieldValue("description_of_operations", finalLines);
    };

  const handleAdditionalNameInsured = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const value = checked ? "Y" : "N";
    if (value === "Y") {
      setValues({
        ...values,
        additional_name_insured_yes_or_no: value,
        additional_name_insured_name: values.certificate_holder_name,
        additional_name_insured_mailing_address:
          values.certificate_holder_address,
      });
    } else {
      setValues({
        ...values,
        additional_name_insured_yes_or_no: value,
      });
    }
  };
  React.useEffect(() => {
    return () => {
      dispatch(clearBusinessPolicyRestrutant());
    };
  }, []);
  React.useEffect(() => {
    if (customerPolicyId && liabilityNumber && !disableApiCalls) {
      dispatch(fetchBusinessLiabilityAsync(customerPolicyId, liabilityNumber));
    }
  }, [customerPolicyId, liabilityNumber]);

  const handleUserSelect = (newValue: any) => {
    const { user_fact_id, branch_name, business_number, email } = newValue;

    setFieldValue("certificate_authorization_assigned_to", user_fact_id);
    setFieldValue("certificate_authorization_branch_name", branch_name || "");
    setFieldValue("certificate_authorization_phone_number", business_number);
    setFieldValue("certificate_authorization_email", email);
  };

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({
      ...businessPolicyLiability,
      brokerage_full_name: values.brokerage_full_name || branch.name,
      brokerage_mailing_address:
        values.brokerage_mailing_address ||
        getFullAddres(
          branch.address,
          branch.unit_or_suite,
          branch.city,
          branch.province_or_state,
          branch.country,
          branch.postal_code
        ),
      brokerage_customer_code:
        values.brokerage_customer_code || policyAdmin.data.customer_code,
      // certificate_authorization_branch_name: branch.name,
      // certificate_authorization_email:
      //   businessPolicyLiability.certificate_authorization_email ||
      //   branch.cl_email,
      // certificate_authorization_phone_number:
      //   businessPolicyLiability.certificate_authorization_phone_number ||
      //   branch.telephone,
      certificate_authorization_assigned_to:
        businessPolicyLiability.certificate_authorization_assigned_to ||
        currentUserId,
      producer_one_id: policyAdmin?.data?.producer_one,
      producer_one_code: policyAdmin?.data?.producer_one_code,
    });
  }, [businessPolicyLiability, branch, policyAdmin]);

  React.useEffect(() => {
    return () => {
      dispatch(clearBusinessPolicyFormsState());
    };
  }, []);

  /* ---------------------- small render helpers ---------------------- */

  const policyOptions = customerPolicies
    .filter((p) => p.policy_number !== null)
    .map((policy) => ({
      label: policy.policy_number,
      value: policy.policy_number || "",
    }));

  // Shared sx for the right-hand "meta" cells (Insurance Company / Policy /
  // Effective / Expiry) in the coverage table.
  const metaCellSx = {
    verticalAlign: "top",
    backgroundColor: "action.hover",
    borderLeft: "1px solid",
    borderColor: "divider",
  } as const;

  // Icon buttons that live inside a SectionBar (white on the dark-blue bar).
  const barIconButtonSx = {
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.6)",
    p: 0.25,
    "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
  } as const;

  // A group-title row inside the coverage table (bold) carrying the editable
  // Insurance Company / Policy / Effective / Expiry meta cells for a section.
  const renderGroupTitleRow = (opts: {
    title: string;
    metaBase: string;
    insurance_company?: any;
    policy_number?: any;
    effective_date?: any;
    expiry_date?: any;
  }) => (
    <TableRow sx={{ backgroundColor: "#eef3f7" }}>
      <TableCell sx={{ width: 28 }} />
      <TableCell>
        <Typography fontWeight={700}>{opts.title}</Typography>
      </TableCell>
      <TableCell sx={{ ...metaCellSx, width: 280 }}>
        <CustomTextField
          variant="outlined"
          size="small"
          type="text"
          fullWidth
          placeholder="Insurance Company"
          value={opts.insurance_company || ""}
          onChange={(e) =>
            setFieldValue(`${opts.metaBase}.insurance_company`, e.target.value)
          }
          sx={{ mb: 0.5 }}
        />
        <CustomTextField
          variant="outlined"
          size="small"
          type="text"
          fullWidth
          placeholder="Policy Number"
          value={opts.policy_number || ""}
          onChange={(e) =>
            setFieldValue(`${opts.metaBase}.policy_number`, e.target.value)
          }
        />
      </TableCell>
      <TableCell sx={metaCellSx}>
        <CustomDatePicker
          fullWidth
          value={opts.effective_date || ""}
          onChange={(newValue) =>
            setFieldValue(`${opts.metaBase}.effective_date`, newValue)
          }
        />
      </TableCell>
      <TableCell sx={metaCellSx}>
        <CustomDatePicker
          fullWidth
          value={opts.expiry_date || ""}
          onChange={(newValue) =>
            setFieldValue(`${opts.metaBase}.expiry_date`, newValue)
          }
        />
      </TableCell>
      <TableCell align="center" sx={{ width: 150 }}>
        <Typography fontWeight={700}>Deductible</Typography>
      </TableCell>
      <TableCell align="center" sx={{ width: 160 }}>
        <Typography fontWeight={700}>Amount</Typography>
      </TableCell>
    </TableRow>
  );

  // A fixed coverage line driven by a Y/N checkbox flag in
  // commercial_general_liability_section_two. `label` can be a node (used for
  // the Personal/Advertising injury dropdown).
  const renderCglTwoRow = (opts: {
    checkKey: keyof IBusinessPolicyLiability["commercial_general_liability_section_two"];
    deductibleKey: keyof IBusinessPolicyLiability["commercial_general_liability_section_two"];
    amountKey: keyof IBusinessPolicyLiability["commercial_general_liability_section_two"];
    label: React.ReactNode;
  }) => {
    const sec = values.commercial_general_liability_section_two;
    return (
      <TableRow>
        <TableCell sx={{ width: 28 }}>
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={sec[opts.checkKey] === "Y"}
            onChange={(e) =>
              setFieldValue(
                `commercial_general_liability_section_two.${String(
                  opts.checkKey
                )}`,
                e.target.checked ? "Y" : "N"
              )
            }
          />
        </TableCell>
        <TableCell colSpan={4}>
          {typeof opts.label === "string" ? (
            <Typography variant="body1">{opts.label}</Typography>
          ) : (
            opts.label
          )}
        </TableCell>
        <TableCell>
          <CustomTextField
            name={`commercial_general_liability_section_two.${String(
              opts.deductibleKey
            )}`}
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={sec[opts.deductibleKey] as string | number}
            onChange={handleChange}
          />
        </TableCell>
        <TableCell>
          <CustomTextField
            name={`commercial_general_liability_section_two.${String(
              opts.amountKey
            )}`}
            variant="outlined"
            size="small"
            type="text"
            fullWidth
            value={sec[opts.amountKey] as string | number}
            onChange={handleChange}
          />
        </TableCell>
      </TableRow>
    );
  };

  // A simple coverage line with a free-text/label cell and deductible/amount
  // cells wired to arbitrary field name paths.
  const renderCoverageRow = (opts: {
    rowKey: string;
    label: React.ReactNode;
    checkbox?: React.ReactNode;
    deductibleName: string;
    deductibleValue: any;
    amountName: string;
    amountValue: any;
  }) => (
    <TableRow key={opts.rowKey}>
      <TableCell sx={{ width: 28 }}>{opts.checkbox}</TableCell>
      <TableCell colSpan={4}>
        {typeof opts.label === "string" ? (
          <Typography variant="body1">{opts.label}</Typography>
        ) : (
          opts.label
        )}
      </TableCell>
      <TableCell>
        <CustomTextField
          name={opts.deductibleName}
          variant="outlined"
          size="small"
          type="text"
          fullWidth
          value={opts.deductibleValue}
          onChange={handleChange}
        />
      </TableCell>
      <TableCell>
        <CustomTextField
          name={opts.amountName}
          variant="outlined"
          size="small"
          type="text"
          fullWidth
          value={opts.amountValue}
          onChange={handleChange}
        />
      </TableCell>
    </TableRow>
  );

  // Centered "Add coverage" footer row for dynamic coverage groups.
  const renderAddCoverageRow = (onAdd: () => void) => (
    <TableRow>
      <TableCell colSpan={7} sx={{ py: 1, textAlign: "center" }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Add fontSize="small" />}
          onClick={onAdd}
          sx={{
            textTransform: "none",
            borderStyle: "dashed",
            borderRadius: 2,
            px: 3,
            color: "primary.main",
            borderColor: "primary.main",
            "&:hover": {
              borderStyle: "dashed",
              backgroundColor: "primary.main",
              color: "#fff",
            },
          }}
        >
          Add coverage
        </Button>
      </TableCell>
    </TableRow>
  );

  // Dynamic "type of insurance / coverage / deductible / amount" rows used by
  // the CGL "Add More" (section_three) and Other Liability (section_two)
  // blocks. Both share the same ICommercialSectionDropdown-ish shape.
  const renderDynamicInsuranceRows = (
    rootKey:
      | "commercial_general_liability_section_three"
      | "other_liability_section_two"
  ) => {
    const rows = values[rootKey];
    const updateCell = (index: number, column: string, value: any) => {
      const next = produce(rows, (draft) => {
        // @ts-ignore - dynamic column access on the row shape
        draft[index][column] = value;
      });
      setFieldValue(rootKey, next);
    };
    const addRow = () => {
      const next = [
        ...rows,
        {
          key: getUniqueId(),
          type_of_insurance_check: "",
          type_of_insurance: "",
          coverage: "",
          deductible: "",
          amount: "",
        },
      ];
      setFieldValue(rootKey, next);
    };
    const removeRow = (index: number) => {
      const next = produce(rows, (draft) => {
        draft.splice(index, 1);
      });
      setFieldValue(rootKey, next);
    };
    return (
      <>
        {rows.map((item, index) => (
          <TableRow key={item.key}>
            <TableCell sx={{ width: 28 }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={item.type_of_insurance_check === "Y"}
                onChange={(e) =>
                  updateCell(
                    index,
                    "type_of_insurance_check",
                    e.target.checked ? "Y" : "N"
                  )
                }
              />
            </TableCell>
            <TableCell colSpan={2}>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                placeholder="Type of Insurance"
                value={item.type_of_insurance}
                onChange={(e) =>
                  updateCell(index, "type_of_insurance", e.target.value)
                }
              />
            </TableCell>
            <TableCell colSpan={2}>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                placeholder="Coverage"
                value={item.coverage}
                onChange={(e) =>
                  updateCell(index, "coverage", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <CustomTextField
                variant="outlined"
                size="small"
                type="text"
                fullWidth
                value={item.deductible}
                onChange={(e) =>
                  updateCell(index, "deductible", e.target.value)
                }
              />
            </TableCell>
            <TableCell>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CustomTextField
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={item.amount}
                  onChange={(e) =>
                    updateCell(index, "amount", e.target.value)
                  }
                />
                <Tooltip title="Remove this coverage">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeRow(index)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
        {renderAddCoverageRow(addRow)}
      </>
    );
  };

  const cgl2 = values.commercial_general_liability_section_two;

  return (
    <PageLoader
      loading={businessPolicyLiabilityLoading === LoadState.InProgress}
    >
      <StandadCard
        sx={{ mt: 2 }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Certificate of Liability Insurance" : ""}
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            {/* ---- Linked policies ---- */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>CGL Policy</CustomFormLabel>
                <AutoComplete
                  value={values.cgl_policy_number}
                  onChange={(newValue) => handleCGLPolicy(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>Non-Owned Automobiles Policy</CustomFormLabel>
                <AutoComplete
                  value={values.non_owned_automobiles_policy}
                  onChange={(newValue) => handleNonOwnedAutomobile(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>Hired Automobiles</CustomFormLabel>
                <AutoComplete
                  value={values.hired_automobiles}
                  onChange={(newValue) => handleHiredAutomobile(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>Automobile Policy</CustomFormLabel>
                <AutoComplete
                  value={values.automobile_policy}
                  onChange={(newValue) => handleAutomobilemobile(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>Umbrella/Excess Liability Policy</CustomFormLabel>
                <AutoComplete
                  value={values.umbrella_or_excess_liability_policy}
                  onChange={(newValue) => handleExcessPolicy(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <CustomFormLabel>Other Policy</CustomFormLabel>
                <AutoComplete
                  value={values.other_policy}
                  onChange={(newValue) => handleOtherPolicy(newValue)}
                  placeholder="Select one"
                  options={policyOptions}
                ></AutoComplete>
              </Grid>
            </Grid>

            {/* Select vehicle */}
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <CustomFormLabel>Unit No/VIN</CustomFormLabel>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    name="vehicle_unit_number_or_vin"
                    value={values.vehicle_unit_number_or_vin}
                    onChange={handleChange}
                    displayEmpty
                    fullWidth
                    placeholder="Select one"
                    options={vehicleDropdownlist}
                  ></ControlledCustomSelect>
                </Grid>
              </Grid>
            </Box>

            {/* ---- 1. Insured + 2. Brokerage (side-by-side) ---- */}
            <Grid container spacing={0} sx={{ mt: 2 }}>
              {/* Section 1 - Insured / Certificate Holder */}
              <Grid item xs={12} md={6}>
                <SectionBar no={1} title="Certificate Holder" />
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderTop: "none",
                    p: 1,
                    height: "100%",
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel sx={{ mt: 0 }}>Name</CustomFormLabel>
                      <CustomTextField
                        name="certificate_holder_name"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        value={values.certificate_holder_name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel sx={{ mt: 0 }}>
                        Full Address
                      </CustomFormLabel>
                      <CustomTextField
                        name="certificate_holder_address"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        value={values.certificate_holder_address}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel sx={{ mt: 0 }}>Email</CustomFormLabel>
                      <CustomTextField
                        name="certificate_holder_email"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        value={values.certificate_holder_email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel sx={{ mt: 0 }}>
                        Phone (+1)
                      </CustomFormLabel>
                      <PhoneTextField
                        name="certificate_holder_phone"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={values.certificate_holder_phone}
                        setFieldValue={setFieldValue}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Section 2 - Brokerage */}
              <Grid item xs={12} md={6}>
                <SectionBar
                  no={2}
                  title="Brokerage / Agency Full Name and Mailing Address"
                />
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderTop: "none",
                    borderLeft: { md: "none" },
                    p: 1,
                    height: "100%",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <CustomFormLabel sx={{ mt: 0 }}>Full Name</CustomFormLabel>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      BROKER CLIENT ID: {values.brokerage_customer_code || "—"}
                    </Typography>
                  </Stack>
                  <CustomTextField
                    name="brokerage_full_name"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.brokerage_full_name}
                    disabled
                  />
                  <CustomFormLabel>Mailing Address</CustomFormLabel>
                  <CustomTextField
                    name="brokerage_mailing_address"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    multiline
                    minRows={2}
                    value={values.brokerage_mailing_address}
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>

            {/* ---- 3. Description of Operations ---- */}
            <Box sx={{ mt: 2 }}>
              <SectionBar
                no={3}
                title="Description of Operations / Locations / Automobiles / Special Items"
                right={
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Add line">
                      <IconButton
                        size="small"
                        onClick={handleAdd}
                        sx={barIconButtonSx}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(values.description_of_operations || []).length > 0 && (
                      <Tooltip title="Remove last line">
                        <IconButton
                          size="small"
                          onClick={handleRemove}
                          sx={barIconButtonSx}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                }
              />
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: "none",
                  p: 1,
                }}
              >
                <Grid container spacing={1}>
                  {(values.description_of_operations || []).map(
                    (line, index) => (
                      <Grid item xs={12} sm={6} md={4} key={line.key || index}>
                        <CustomFormLabel sx={{ mt: 0 }}>
                          {line.label}
                        </CustomFormLabel>
                        <CustomTextField
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          value={line.value}
                          onChange={handleDescriptionLine(line.key, index)}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              </Box>
            </Box>

            {/* ---- 4. Coverages ---- */}
            <Box sx={{ mt: 2 }} />
            <SectionBar no={4} title="Coverages" />
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                overflowX: "auto",
                mt: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 28 }} />
                    <TableCell>
                      <Typography fontWeight={600} variant="h6">
                        Type of Insurance / Coverage
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 280 }}>
                      <Typography fontWeight={600} variant="h6">
                        Insurance Company and Policy Number
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 170 }}>
                      <Typography fontWeight={600} variant="h6">
                        Effective
                      </Typography>
                      <Typography variant="caption">YYYY/MM/DD</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 170 }}>
                      <Typography fontWeight={600} variant="h6">
                        Expiry
                      </Typography>
                      <Typography variant="caption">YYYY/MM/DD</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 150 }}>
                      <Typography fontWeight={600} variant="h6">
                        Deductible
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 160 }}>
                      <Typography fontWeight={600} variant="h6">
                        Amount
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* ===== Commercial General Liability ===== */}
                  {renderGroupTitleRow({
                    title: "Commercial General Liability",
                    metaBase: "commercial_general_liability_section_one",
                    insurance_company:
                      values.commercial_general_liability_section_one
                        .insurance_company,
                    policy_number:
                      values.commercial_general_liability_section_one
                        .policy_number,
                    effective_date:
                      values.commercial_general_liability_section_one
                        .effective_date,
                    expiry_date:
                      values.commercial_general_liability_section_one
                        .expiry_date,
                  })}
                  {/* Claims Made OR Occurrence */}
                  <TableRow>
                    <TableCell sx={{ width: 28 }}>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={cgl2.claim_mode === "Y"}
                        onChange={(e) =>
                          setValues({
                            ...values,
                            commercial_general_liability_section_two: {
                              ...cgl2,
                              claim_mode: e.target.checked ? "Y" : "N",
                              occurance: e.target.checked ? "N" : cgl2.occurance,
                            },
                          })
                        }
                      />
                    </TableCell>
                    <TableCell colSpan={4}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        flexWrap="wrap"
                      >
                        <Typography variant="body1">
                          Claims Made (General Aggregate)
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          OR
                        </Typography>
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            size="small"
                            sx={{ p: 0.5 }}
                            checked={cgl2.occurance === "Y"}
                            onChange={(e) =>
                              setValues({
                                ...values,
                                commercial_general_liability_section_two: {
                                  ...cgl2,
                                  occurance: e.target.checked ? "Y" : "N",
                                  claim_mode: e.target.checked
                                    ? "N"
                                    : cgl2.claim_mode,
                                },
                              })
                            }
                          />
                          <Typography variant="body1">
                            Occurrence (Each Occurrence)
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <CustomTextField
                        name="commercial_general_liability_section_two.claim_mode_deductible"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        value={cgl2.claim_mode_deductible}
                        onChange={handleChange}
                      />
                    </TableCell>
                    <TableCell>
                      <CustomTextField
                        name="commercial_general_liability_section_two.claim_mode_amount"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        value={cgl2.claim_mode_amount}
                        onChange={handleChange}
                      />
                    </TableCell>
                  </TableRow>
                  {renderCglTwoRow({
                    checkKey: "general_aggregate",
                    deductibleKey: "general_aggregate_deductible",
                    amountKey: "general_aggregate_amount",
                    label: "General Aggregate",
                  })}
                  {renderCoverageRow({
                    rowKey: "cgl-each-occurrence",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={cgl2.occurance === "Y"}
                        onChange={(e) =>
                          setFieldValue(
                            "commercial_general_liability_section_two.occurance",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "Each Occurrence",
                    deductibleName:
                      "commercial_general_liability_section_two.occurance_deductible",
                    deductibleValue: cgl2.occurance_deductible,
                    amountName:
                      "commercial_general_liability_section_two.occurance_amount",
                    amountValue: cgl2.occurance_amount,
                  })}
                  {renderCglTwoRow({
                    checkKey: "products_completed_oprations",
                    deductibleKey:
                      "products_completed_oprations_deductible",
                    amountKey: "products_completed_oprations_amount",
                    label:
                      "Products and Completed Operations Aggregate",
                  })}
                  {renderCglTwoRow({
                    checkKey: "personal_advertising_injury",
                    deductibleKey: "personal_advertising_injury_deductible",
                    amountKey: "personal_advertising_injury_amount",
                    label: (
                      <ControlledCustomSelect
                        variant="outlined"
                        size="small"
                        fullWidth
                        displayEmpty
                        name="commercial_general_liability_section_two.personal_advertising_injury_value"
                        value={cgl2.personal_advertising_injury_value}
                        onChange={handleChange}
                        placeholder="Personal / Advertising Injury Liability"
                        options={[
                          "Personal Injury Liability",
                          "Personal and Advertising Injury Liability",
                        ].map((o) => ({ label: o, value: o }))}
                      />
                    ),
                  })}
                  {renderCglTwoRow({
                    checkKey: "waiver_of_subrogation",
                    deductibleKey: "waiver_of_subrogation_deductible",
                    amountKey: "waiver_of_subrogation_amount",
                    label: "Waiver of Subrogation",
                  })}
                  {renderCglTwoRow({
                    checkKey: "pollution_Liability_Extension",
                    deductibleKey:
                      "pollution_Liability_Extension_deductible",
                    amountKey: "pollution_Liability_Extension_amount",
                    label: "Pollution Liability Extension",
                  })}
                  {renderCglTwoRow({
                    checkKey: "empolyer_liability",
                    deductibleKey: "empolyer_liability_deductible",
                    amountKey: "empolyer_liability_amount",
                    label: "Employer Liability",
                  })}
                  {renderCglTwoRow({
                    checkKey: "medical_payments",
                    deductibleKey: "medical_payments_deductible",
                    amountKey: "medical_payments_amount",
                    label: "Medical Payments",
                  })}
                  {renderCglTwoRow({
                    checkKey: "tenants_Legal_Liability",
                    deductibleKey: "tenants_Legal_Liability_deductible",
                    amountKey: "tenants_Legal_Liability_amount",
                    label: "Tenants Legal Liability",
                  })}
                  {renderCglTwoRow({
                    checkKey: "cross_liability",
                    deductibleKey: "cross_liability_deductible",
                    amountKey: "cross_liability_amount",
                    label: "Cross Liability",
                  })}
                  {renderDynamicInsuranceRows(
                    "commercial_general_liability_section_three"
                  )}

                  {/* ===== Non-Owned Automobiles ===== */}
                  {renderGroupTitleRow({
                    title: "Non-Owned Automobiles",
                    metaBase: "non_owned_automobiles_section_one",
                    insurance_company:
                      values.non_owned_automobiles_section_one
                        .insurance_company,
                    policy_number:
                      values.non_owned_automobiles_section_one.policy_number,
                    effective_date:
                      values.non_owned_automobiles_section_one.effective_date,
                    expiry_date:
                      values.non_owned_automobiles_section_one.expiry_date,
                  })}
                  {renderCoverageRow({
                    rowKey: "non-owned-auto",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.non_owned_automobiles_section_two
                            .non_owned === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "non_owned_automobiles_section_two.non_owned",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "Non-Owned Automobiles",
                    deductibleName:
                      "non_owned_automobiles_section_two.non_owned_deductible",
                    deductibleValue:
                      values.non_owned_automobiles_section_two
                        .non_owned_deductible,
                    amountName:
                      "non_owned_automobiles_section_two.non_ownder_amount",
                    amountValue:
                      values.non_owned_automobiles_section_two
                        .non_ownder_amount,
                  })}

                  {/* ===== Hired Automobiles ===== */}
                  {renderGroupTitleRow({
                    title: "Hired Automobiles",
                    metaBase: "hired_automobiles_section_one",
                    insurance_company:
                      values.hired_automobiles_section_one.insurance_company,
                    policy_number:
                      values.hired_automobiles_section_one.policy_number,
                    effective_date:
                      values.hired_automobiles_section_one.effective_date,
                    expiry_date:
                      values.hired_automobiles_section_one.expiry_date,
                  })}
                  {renderCoverageRow({
                    rowKey: "hired-auto",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.hired_automobiles_section_two
                            .hired_type_of_insurance === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "hired_automobiles_section_two.hired_type_of_insurance",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "Hired Automobiles",
                    deductibleName:
                      "hired_automobiles_section_two.hired_deductible",
                    deductibleValue:
                      values.hired_automobiles_section_two.hired_deductible,
                    amountName:
                      "hired_automobiles_section_two.hired_amount",
                    amountValue:
                      values.hired_automobiles_section_two.hired_amount,
                  })}

                  {/* ===== Automobile Liability ===== */}
                  {renderGroupTitleRow({
                    title: "Automobile Liability",
                    metaBase: "automobile_liability_section_one",
                    insurance_company:
                      values.automobile_liability_section_one
                        .insurance_company,
                    policy_number:
                      values.automobile_liability_section_one.policy_number,
                    effective_date:
                      values.automobile_liability_section_one.effective_date,
                    expiry_date:
                      values.automobile_liability_section_one.expiry_date,
                  })}
                  {renderCoverageRow({
                    rowKey: "auto-described",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.automobile_liability_section_two
                            .described_automobiles === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "automobile_liability_section_two.described_automobiles",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label:
                      "Described Automobiles — Bodily Injury & Property Damage Combined",
                    deductibleName:
                      "automobile_liability_section_two.described_automobiles_deductible",
                    deductibleValue:
                      values.automobile_liability_section_two
                        .described_automobiles_deductible,
                    amountName:
                      "automobile_liability_section_two.described_automobiles_amount",
                    amountValue:
                      values.automobile_liability_section_two
                        .described_automobiles_amount,
                  })}
                  {renderCoverageRow({
                    rowKey: "auto-all-owned",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.automobile_liability_section_two
                            .all_owned_automobiles === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "automobile_liability_section_two.all_owned_automobiles",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "All Owned Automobiles — Bodily Injury (Per Person)",
                    deductibleName:
                      "automobile_liability_section_two.all_owned_automobiles_deductible",
                    deductibleValue:
                      values.automobile_liability_section_two
                        .all_owned_automobiles_deductible,
                    amountName:
                      "automobile_liability_section_two.all_owned_automobiles_amount",
                    amountValue:
                      values.automobile_liability_section_two
                        .all_owned_automobiles_amount,
                  })}
                  {renderCoverageRow({
                    rowKey: "auto-leased",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.automobile_liability_section_two
                            .leased_automobiles === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "automobile_liability_section_two.leased_automobiles",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "Leased Automobiles — Bodily Injury (Per Accident)",
                    deductibleName:
                      "automobile_liability_section_two.leased_automobiles_deductible",
                    deductibleValue:
                      values.automobile_liability_section_two
                        .leased_automobiles_deductible,
                    amountName:
                      "automobile_liability_section_two.leased_automobiles_amount",
                    amountValue:
                      values.automobile_liability_section_two
                        .leased_automobiles_amount,
                  })}
                  {renderCoverageRow({
                    rowKey: "auto-property-damage",
                    label: "Property Damage",
                    deductibleName:
                      "automobile_liability_section_two.property_damage_deductible",
                    deductibleValue:
                      values.automobile_liability_section_two
                        .property_damage_deductible,
                    amountName:
                      "automobile_liability_section_two.property_damage_amount",
                    amountValue:
                      values.automobile_liability_section_two
                        .property_damage_amount,
                  })}

                  {/* ===== Excess Liability ===== */}
                  {renderGroupTitleRow({
                    title: "Excess Liability",
                    metaBase: "excess_liability_section_one",
                    insurance_company:
                      values.excess_liability_section_one.insurance_company,
                    policy_number:
                      values.excess_liability_section_one.policy_number,
                    effective_date:
                      values.excess_liability_section_one.effective_date,
                    expiry_date:
                      values.excess_liability_section_one.expiry_date,
                  })}
                  {renderCoverageRow({
                    rowKey: "excess-umbrella",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.excess_liability_section_two.umbrella_form ===
                          "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "excess_liability_section_two.umbrella_form",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: "Umbrella Form — Each Occurrence Aggregate Coverage",
                    deductibleName:
                      "excess_liability_section_two.umbrella_form_deductible",
                    deductibleValue:
                      values.excess_liability_section_two
                        .umbrella_form_deductible,
                    amountName:
                      "excess_liability_section_two.umbrella_form_amount",
                    amountValue:
                      values.excess_liability_section_two.umbrella_form_amount,
                  })}
                  {renderCoverageRow({
                    rowKey: "excess-aggregate",
                    label: "Aggregate",
                    deductibleName:
                      "excess_liability_section_two.aggreate_deductible",
                    deductibleValue:
                      values.excess_liability_section_two.aggreate_deductible,
                    amountName:
                      "excess_liability_section_two.aggreate_amount",
                    amountValue:
                      values.excess_liability_section_two.aggreate_amount,
                  })}
                  {renderCoverageRow({
                    rowKey: "excess-extra",
                    checkbox: (
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={
                          values.excess_liability_section_two.extra === "Y"
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "excess_liability_section_two.extra",
                            e.target.checked ? "Y" : "N"
                          )
                        }
                      />
                    ),
                    label: (
                      <Stack direction="row" spacing={1}>
                        <CustomTextField
                          name="excess_liability_section_two.extra_type_of_insurance"
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          placeholder="Type of Insurance"
                          value={
                            values.excess_liability_section_two
                              .extra_type_of_insurance
                          }
                          onChange={handleChange}
                        />
                        <CustomTextField
                          name="excess_liability_section_two.extra_coverage"
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          placeholder="Coverage"
                          value={
                            values.excess_liability_section_two.extra_coverage
                          }
                          onChange={handleChange}
                        />
                      </Stack>
                    ),
                    deductibleName:
                      "excess_liability_section_two.extra_deductible",
                    deductibleValue:
                      values.excess_liability_section_two.extra_deductible,
                    amountName: "excess_liability_section_two.extra_amount",
                    amountValue:
                      values.excess_liability_section_two.extra_amount,
                  })}

                  {/* ===== Other Liability ===== */}
                  {renderGroupTitleRow({
                    title: "Other Liability (Specify)",
                    metaBase: "other_liability_section_one",
                    insurance_company:
                      values.other_liability_section_one.insurance_company,
                    policy_number:
                      values.other_liability_section_one.policy_number,
                    effective_date:
                      values.other_liability_section_one.effective_date,
                    expiry_date:
                      values.other_liability_section_one.expiry_date,
                  })}
                  {renderDynamicInsuranceRows("other_liability_section_two")}
                </TableBody>
              </Table>
            </Box>

            {/* ---- 5. Cancellation ---- */}
            <Box sx={{ mt: 2 }} />
            <SectionBar no={5} title="Cancellation" />
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderTop: "none",
                p: 1,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <CustomFormLabel sx={{ mt: 0 }}>No. of Days</CustomFormLabel>
                  <CustomTextField
                    name="cancelation_number_of_days"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.cancelation_number_of_days}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ---- 6. Additional Named Insured ---- */}
            <Box sx={{ mt: 2 }} />
            <SectionBar no={6} title="Additional Insured Name and Mailing Address" />
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderTop: "none",
                p: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={values.additional_name_insured_yes_or_no === "Y"}
                  onChange={handleAdditionalNameInsured}
                />
                <Typography variant="body1">
                  Print Certificate Holder as Additional Named Insured (Y/N)
                </Typography>
              </Stack>
              <Grid container spacing={1}>
                <Grid item xs={12} md={5}>
                  <CustomFormLabel sx={{ mt: 0 }}>Name</CustomFormLabel>
                  <CustomTextField
                    name="additional_name_insured_name"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.additional_name_insured_name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <CustomFormLabel sx={{ mt: 0 }}>
                    Mailing Address
                  </CustomFormLabel>
                  <CustomTextField
                    name="additional_name_insured_mailing_address"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.additional_name_insured_mailing_address}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ---- 7. Certificate Authorization ---- */}
            <Box sx={{ mt: 2 }} />
            <SectionBar no={7} title="Certificate Authorization" />
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderTop: "none",
                p: 1,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} md={3}>
                  <UsersAutoSearchByRole
                    label="Assigned To"
                    value={values.certificate_authorization_assigned_to}
                    onSelect={handleUserSelect}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Branch Name</CustomFormLabel>
                  <CustomTextField
                    name="certificate_authorization_branch_name"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.certificate_authorization_branch_name}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Phone (+1)</CustomFormLabel>
                  <PhoneTextField
                    name="certificate_authorization_phone_number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.certificate_authorization_phone_number}
                    error={
                      errors.certificate_authorization_phone_number
                        ? true
                        : false
                    }
                    helperText={errors.certificate_authorization_phone_number}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Fax</CustomFormLabel>
                  <CustomTextField
                    name="certificate_authorization_fax_number"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.certificate_authorization_fax_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Email</CustomFormLabel>
                  <CustomTextField
                    name="certificate_authorization_email"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.certificate_authorization_email}
                    error={
                      errors.certificate_authorization_email ? true : false
                    }
                    helperText={errors.certificate_authorization_email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Date</CustomFormLabel>
                  <CustomDatePicker
                    fullWidth
                    value={values.certificate_authorization_date || ""}
                    onChange={(newValue) => {
                      setFieldValue(
                        "certificate_authorization_date",
                        newValue
                      );
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomFormLabel sx={{ mt: 0 }}>Status</CustomFormLabel>
                  <ControlledCustomSelect
                    fullWidth
                    value={values.status}
                    name="status"
                    onChange={handleChange}
                    placeholder="Select one"
                    displayEmpty
                    options={COMMON_STATUS}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack mt={4}>
                    <CustomCheckBoxWithLabel
                      checked={values.is_template === "Y"}
                      onChange={(_, checked) => {
                        setFieldValue("is_template", checked ? "Y" : "N");
                      }}
                      label="Save as Template"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box marginTop={3}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.BUSINESS_POLICY}
                type="submit"
                variant="contained"
                disabled={saveLoading}
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
  );
};
